import { useEffect, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "sonner";
import { Bold, Italic, List, ListOrdered, Quote, AtSign, Heading2, Heading3, Link2, ImagePlus, Loader2, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { MentionMenu } from "@/components/journal/MentionMenu";
import { JournalMention, mentionSuggestion, mentionRenderHTML } from "@/components/journal/JournalEditor";

export const MAX_INLINE_IMAGES = 5;

/* Uploads one photo: the server verifies, re-encodes and stores it, returning an id. */
export async function uploadPhoto(file) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/blog/images", form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

function ImageView({ node, updateAttributes, deleteNode, selected }) {
  return (
    <NodeViewWrapper as="figure" className={`my-4 rounded-2xl border ${selected ? "border-gold" : "border-transparent"} p-1`} data-testid="editor-image">
      <img src={`${API}/blog/images/${node.attrs.id}`} alt={node.attrs.alt || ""} className="mx-auto max-h-96 rounded-xl object-contain" draggable={false} />
      <div className="mt-2 flex items-center gap-2">
        <input value={node.attrs.caption || ""} onChange={(e) => updateAttributes({ caption: e.target.value, alt: e.target.value })} placeholder="Add a caption (optional)" className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-xs" data-testid="editor-image-caption" />
        <button type="button" onClick={deleteNode} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Remove photo" data-testid="editor-image-remove"><Trash2 className="h-4 w-4" /></button>
      </div>
    </NodeViewWrapper>
  );
}

const BlogImage = Node.create({
  name: "blogImage",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() { return { id: { default: null }, alt: { default: "" }, caption: { default: "" } }; },
  parseHTML() { return [{ tag: "figure[data-blog-image]" }]; },
  renderHTML({ HTMLAttributes }) { return ["figure", mergeAttributes({ "data-blog-image": "" }, HTMLAttributes)]; },
  addNodeView() { return ReactNodeViewRenderer(ImageView); },
});

const countImages = (doc) => JSON.stringify(doc || {}).split('"type":"blogImage"').length - 1;

/* Blog post editor: journal editor + headings, safe links and inline photos. `value` is a Tiptap JSON doc. */
export default function BlogEditor({ value, onChange, minHeight = 360, testId = "blog-editor" }) {
  const [mention, setMention] = useState(null);
  const [uploading, setUploading] = useState(false);
  const menuRef = useRef(null);
  const reqRef = useRef(0);
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, codeBlock: false, code: false, horizontalRule: false, link: { openOnClick: false, autolink: true, defaultProtocol: "https", protocols: ["http", "https"], HTMLAttributes: { rel: "nofollow noopener noreferrer", target: "_blank" } } }),
      Placeholder.configure({ placeholder: "Share your perspective… Type @ to link a verse, a question or a commentary." }),
      JournalMention.configure({ deleteTriggerWithBackspace: true, renderText: ({ node }) => node.attrs.label, renderHTML: mentionRenderHTML, suggestion: mentionSuggestion(setMention, menuRef, reqRef) }),
      BlogImage,
    ],
    content: value || { type: "doc", content: [{ type: "paragraph" }] },
    editorProps: { attributes: { class: "journal-prose blog-prose focus:outline-none", "data-testid": `${testId}-content` } },
    onUpdate: ({ editor: ed }) => onChange?.(ed.getJSON()),
  });

  useEffect(() => () => editor?.destroy(), [editor]);
  if (!editor) return <div style={{ minHeight }} className="animate-pulse rounded-xl bg-muted" />;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link to (https://…)", prev);
    if (url === null) return;
    if (!url.trim()) { editor.chain().focus().unsetLink().run(); return; }
    if (!/^https?:\/\//i.test(url.trim())) { toast.error("Links must start with http:// or https://"); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const addPhoto = async (file) => {
    if (!file) return;
    if (countImages(editor.getJSON()) >= MAX_INLINE_IMAGES) { toast.error(`Up to ${MAX_INLINE_IMAGES} photos per post.`); return; }
    setUploading(true);
    try {
      const img = await uploadPhoto(file);
      editor.chain().focus().insertContent({ type: "blogImage", attrs: { id: img.id, alt: "", caption: "" } }).run();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const Tool = ({ active, onClick, label, icon: Icon, disabled }) => (
    <button type="button" disabled={disabled} onMouseDown={(e) => { e.preventDefault(); onClick(); }} aria-label={label} title={label} data-testid={`${testId}-tool-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-md p-1.5 transition-colors disabled:opacity-50 ${active ? "bg-gold/20 text-gold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );

  return (
    <div className="rounded-2xl border border-border bg-background" data-testid={testId}>
      <div className="sticky top-16 z-10 flex flex-wrap items-center gap-0.5 rounded-t-2xl border-b border-border bg-background/95 px-2 py-1.5 backdrop-blur">
        <Tool label="Heading" icon={Heading2} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <Tool label="Subheading" icon={Heading3} active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <span className="mx-1 h-4 w-px bg-border" />
        <Tool label="Bold" icon={Bold} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} />
        <Tool label="Italic" icon={Italic} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <Tool label="Bullet list" icon={List} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <Tool label="Numbered list" icon={ListOrdered} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
        <Tool label="Quote" icon={Quote} active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
        <Tool label="Link" icon={Link2} active={editor.isActive("link")} onClick={setLink} />
        <span className="mx-1 h-4 w-px bg-border" />
        <Tool label="Mention" icon={AtSign} onClick={() => editor.chain().focus().insertContent("@").run()} />
        <Tool label="Photo" icon={uploading ? Loader2 : ImagePlus} disabled={uploading} onClick={() => fileRef.current?.click()} />
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => addPhoto(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
        <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:block">@ links a verse, question or commentary · up to {MAX_INLINE_IMAGES} photos</span>
      </div>
      <div className="px-5 py-4" style={{ minHeight }} onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </div>
      {mention && <MentionMenu ref={menuRef} items={mention.items} loading={mention.loading} query={mention.query} clientRect={mention.clientRect} command={mention.command} />}
    </div>
  );
}
